import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { Plus, Trash2, ChevronUp, ChevronDown, Search, Check, Dumbbell, X, CalendarPlus } from 'lucide-react';
import type { Exercise } from '@techsofit/api-types';
import { ApiClientError } from '@techsofit/app-kit';
import { Button, Card, Input, Select, Textarea, Modal, Badge, useConfirm, cn } from '@techsofit/ui';
import { PageHeader } from '../components/PageHeader';
import { ScheduleForm } from '../components/ScheduleForm';
import { useAsync } from '../lib/useAsync';
import { todayIso } from '../lib/format';
import { uid } from '../lib/uid';
import {
  getExercises,
  getExerciseFilters,
  getWorkout,
  createWorkout,
  createCustomExercise,
  createInlineAssignment,
  updateWorkout,
  deleteWorkout,
} from '../lib/gymApi';

interface BuilderItem {
  key: string;
  exerciseId: string;
  exerciseName: string;
  muscleGroups: string[];
  sets: string;
  reps: string;
  load: string;
  rest: string;
  notes: string;
  /** Whether the optional weight/rest fields are revealed for this exercise. */
  more: boolean;
}

/** Build or edit a reusable workout from library exercises. */
export function WorkoutBuilderPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const memberId = params.get('member');
  const memberName = params.get('name') || 'this member';
  const fromId = params.get('from'); // duplicate a previous workout's exercises
  const editing = !!id;
  // "Assign mode": build a one-off for a single member and schedule it in one step.
  const assignMode = !!memberId && !editing;
  const nav = useNavigate();
  const confirm = useConfirm();

  const [name, setName] = useState('');
  const [notes, setNotes] = useState('');
  const [items, setItems] = useState<BuilderItem[]>([]);
  const [scheduledDate, setScheduledDate] = useState(todayIso);
  const [timeLabel, setTimeLabel] = useState(''); // '' = any time; else Morning/Afternoon/Evening
  const [saveAsTemplate, setSaveAsTemplate] = useState(false);
  const [picking, setPicking] = useState(false);
  const [scheduling, setScheduling] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const busy = saving || deleting;

  // Edit loads the workout being edited; assign+from loads a previous one to duplicate.
  useEffect(() => {
    const sourceId = id ?? fromId;
    if (!sourceId) return;
    getWorkout(sourceId).then((w) => {
      setName(w.name);
      setNotes(w.notes ?? '');
      setItems(
        w.items.map((it) => ({
          key: uid(),
          exerciseId: it.exerciseId ?? '',
          exerciseName: it.exerciseName,
          muscleGroups: it.muscleGroups,
          sets: it.sets != null ? String(it.sets) : '',
          reps: it.reps ?? '',
          load: it.load ?? '',
          rest: it.rest ?? '',
          notes: it.notes ?? '',
          more: !!(it.load || it.rest), // reveal weight/rest if the source had them
        })),
      );
    });
  }, [id, fromId]);

  function addExercise(ex: Exercise) {
    setItems((prev) => [
      ...prev,
      {
        key: uid(),
        exerciseId: ex.id,
        exerciseName: ex.name,
        muscleGroups: ex.muscleGroups,
        sets: '3',
        reps: '10',
        load: '',
        rest: '',
        notes: '',
        more: false,
      },
    ]);
  }

  // Add an exercise that isn't in the library, used just for this workout (no library row).
  function addAdHoc(exName: string) {
    setItems((prev) => [
      ...prev,
      { key: uid(), exerciseId: '', exerciseName: exName, muscleGroups: [], sets: '3', reps: '10', load: '', rest: '', notes: '', more: false },
    ]);
  }

  function patch(key: string, field: keyof BuilderItem, value: string) {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, [field]: value } : i)));
  }

  // Sets are a positive whole number — strip anything else (no negatives, no decimals).
  function patchSets(key: string, value: string) {
    patch(key, 'sets', value.replace(/[^0-9]/g, ''));
  }

  function toggleMore(key: string) {
    setItems((prev) => prev.map((i) => (i.key === key ? { ...i, more: !i.more } : i)));
  }

  function remove(key: string) {
    setItems((prev) => prev.filter((i) => i.key !== key));
  }

  function move(key: string, dir: -1 | 1) {
    setItems((prev) => {
      const idx = prev.findIndex((i) => i.key === key);
      const to = idx + dir;
      if (idx < 0 || to < 0 || to >= prev.length) return prev;
      const next = [...prev];
      [next[idx], next[to]] = [next[to], next[idx]];
      return next;
    });
  }

  async function save() {
    setError(null);
    if (!name.trim()) {
      setError('Give the workout a name.');
      return;
    }
    if (items.length === 0) {
      setError('Add at least one exercise.');
      return;
    }
    setSaving(true);
    // A line is a library exercise (id) or a typed-in one-off (name, no id).
    const payloadItems = items.map((i) => ({
      exerciseId: i.exerciseId || null,
      name: i.exerciseId ? null : i.exerciseName.trim(),
      sets: i.sets ? Number(i.sets) : null,
      reps: i.reps.trim() || null,
      load: i.load.trim() || null,
      rest: i.rest.trim() || null,
      notes: i.notes.trim() || null,
    }));
    try {
      if (assignMode) {
        await createInlineAssignment({
          name: name.trim(),
          notes: notes.trim() || null,
          items: payloadItems,
          scheduledDate,
          timeLabel: timeLabel || null,
          audienceType: 'MEMBER',
          audienceId: memberId!,
          openSession: false,
          saveAsTemplate,
        });
        nav(`/members/${memberId}`);
      } else {
        const req = { name: name.trim(), notes: notes.trim() || null, items: payloadItems };
        if (editing) await updateWorkout(id!, req);
        else await createWorkout(req);
        nav('/workouts');
      }
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not save the workout.');
      setSaving(false);
    }
  }

  async function removeWorkout() {
    if (!id) return;
    const ok = await confirm({
      title: `Delete "${name}"?`,
      description: "This can't be undone.",
      confirmLabel: 'Delete',
      tone: 'danger',
    });
    if (!ok) return;
    setDeleting(true);
    try {
      await deleteWorkout(id);
      nav('/workouts');
    } catch (err) {
      setError(err instanceof ApiClientError ? err.message : 'Could not delete the workout.');
      setDeleting(false);
    }
  }

  const addedIds = useMemo(() => new Set(items.map((i) => i.exerciseId)), [items]);

  return (
    <section className="animate-fade-up">
      <PageHeader
        eyebrow={editing ? 'Edit workout' : assignMode ? `For ${memberName}` : 'New workout'}
        title={editing ? name || 'Workout' : assignMode ? `Workout for ${memberName}` : 'Build a workout'}
        description={
          assignMode
            ? 'Build it and it gets scheduled for this member. Add library exercises or type your own.'
            : 'Name it, then add exercises from the library and set the sets and reps.'
        }
      />

      <Card className="mb-4 flex flex-col gap-4 p-5">
        <Input label="Workout name" value={name} onChange={(e) => setName(e.target.value)} placeholder="e.g. Arms & Shoulders A" />
        <Textarea label="Notes (optional)" value={notes} onChange={(e) => setNotes(e.target.value)} placeholder="Anything the member should know." />
        {assignMode && (
          <>
            <div className="grid gap-4 sm:grid-cols-2">
              <Input label="Date" type="date" value={scheduledDate} onChange={(e) => setScheduledDate(e.target.value)} />
              <Select label="Time of day" value={timeLabel} onChange={(e) => setTimeLabel(e.target.value)}>
                <option value="">Any time</option>
                <option value="Morning">Morning</option>
                <option value="Afternoon">Afternoon</option>
                <option value="Evening">Evening</option>
              </Select>
            </div>
            <label className="flex w-fit cursor-pointer items-center gap-2.5 text-sm font-medium text-ink-soft">
              <input
                type="checkbox"
                checked={saveAsTemplate}
                onChange={(e) => setSaveAsTemplate(e.target.checked)}
                className="size-4 rounded border-line accent-ink"
              />
              Also save to my reusable workouts
            </label>
          </>
        )}
      </Card>

      <div className="mb-3 flex items-center justify-between">
        <p className="text-sm font-medium text-ink">
          {items.length} {items.length === 1 ? 'exercise' : 'exercises'}
        </p>
        <Button size="sm" variant="ghost" onClick={() => setPicking(true)}>
          <Plus className="size-4" aria-hidden /> Add exercise
        </Button>
      </div>

      {items.length === 0 ? (
        <Card className="flex flex-col items-center gap-3 p-8 text-center">
          <Dumbbell className="size-7 text-muted" aria-hidden />
          <p className="text-sm text-muted">No exercises yet — add some from the library.</p>
          <Button size="sm" onClick={() => setPicking(true)}>
            <Plus className="size-4" aria-hidden /> Add exercise
          </Button>
        </Card>
      ) : (
        <div className="flex flex-col gap-2">
          {items.map((it, idx) => (
            <Card key={it.key} className="p-4">
              <div className="flex items-start gap-2">
                <div className="flex flex-col gap-0.5 pt-0.5">
                  <button type="button" aria-label="Move up" disabled={idx === 0} onClick={() => move(it.key, -1)} className="text-muted hover:text-ink disabled:opacity-30">
                    <ChevronUp className="size-4" aria-hidden />
                  </button>
                  <button type="button" aria-label="Move down" disabled={idx === items.length - 1} onClick={() => move(it.key, 1)} className="text-muted hover:text-ink disabled:opacity-30">
                    <ChevronDown className="size-4" aria-hidden />
                  </button>
                </div>
                <div className="min-w-0 flex-1">
                  <div className="flex items-start justify-between gap-2">
                    <p className="font-medium text-ink">{it.exerciseName}</p>
                    <button type="button" aria-label="Remove" onClick={() => remove(it.key)} className="text-muted hover:text-bad-ink">
                      <Trash2 className="size-4" aria-hidden />
                    </button>
                  </div>
                  <div className="mt-2 grid grid-cols-2 gap-2">
                    <Mini label="Sets" value={it.sets} onChange={(v) => patchSets(it.key, v)} inputMode="numeric" placeholder="3" />
                    <Mini label="Reps" value={it.reps} onChange={(v) => patch(it.key, 'reps', v)} placeholder="e.g. 8-12" />
                  </div>
                  {it.more ? (
                    <div className="mt-2 grid grid-cols-2 gap-2">
                      <Mini label="Weight (optional)" value={it.load} onChange={(v) => patch(it.key, 'load', v)} placeholder="e.g. 20kg" />
                      <Mini label="Rest (optional)" value={it.rest} onChange={(v) => patch(it.key, 'rest', v)} placeholder="e.g. 60s" />
                    </div>
                  ) : (
                    <button
                      type="button"
                      onClick={() => toggleMore(it.key)}
                      className="mt-2 inline-flex items-center gap-1 text-xs font-medium text-ink-soft hover:text-ink"
                    >
                      <Plus className="size-3.5" aria-hidden /> Add weight / rest
                    </button>
                  )}
                </div>
              </div>
            </Card>
          ))}
        </div>
      )}

      {error && (
        <p role="alert" className="mt-4 rounded-control bg-bad-soft px-3.5 py-2 text-sm font-medium text-bad-ink">
          {error}
        </p>
      )}

      <div className="mt-5 flex items-center justify-end gap-2">
        {editing && (
          <Button variant="ghost" onClick={removeWorkout} loading={deleting} disabled={busy} className="mr-auto text-bad-ink hover:bg-bad-soft">
            {!deleting && <Trash2 className="size-4" aria-hidden />} Delete
          </Button>
        )}
        <Button variant="ghost" onClick={() => nav(assignMode ? `/members/${memberId}` : '/workouts')} disabled={busy}>
          Cancel
        </Button>
        {editing && (
          <Button variant="outline" onClick={() => setScheduling(true)} disabled={busy}>
            <CalendarPlus className="size-4" aria-hidden /> Schedule
          </Button>
        )}
        <Button onClick={save} loading={saving} disabled={busy}>
          {!saving && <Check className="size-4" aria-hidden />} {editing ? 'Save' : assignMode ? 'Schedule workout' : 'Create workout'}
        </Button>
      </div>

      {picking && (
        <ExercisePicker addedIds={addedIds} onAdd={addExercise} onAddAdHoc={addAdHoc} onClose={() => setPicking(false)} />
      )}

      {scheduling && id && (
        <ScheduleForm workoutId={id} onClose={() => setScheduling(false)} onSaved={() => setScheduling(false)} />
      )}
    </section>
  );
}

function Mini({
  label,
  value,
  onChange,
  placeholder,
  inputMode,
}: {
  label: string;
  value: string;
  onChange: (v: string) => void;
  placeholder?: string;
  inputMode?: 'numeric' | 'text';
}) {
  return (
    <label className="flex flex-col gap-1">
      <span className="text-xs font-medium text-muted">{label}</span>
      <input
        value={value}
        inputMode={inputMode}
        placeholder={placeholder}
        onChange={(e) => onChange(e.target.value)}
        className="h-9 w-full rounded-control border border-line bg-elevated px-2.5 text-sm text-ink outline-none focus:border-ink"
      />
    </label>
  );
}

function ExercisePicker({
  addedIds,
  onAdd,
  onAddAdHoc,
  onClose,
}: {
  addedIds: Set<string>;
  onAdd: (ex: Exercise) => void;
  onAddAdHoc: (name: string) => void;
  onClose: () => void;
}) {
  const { data: filters } = useAsync(getExerciseFilters);
  const { data: all, loading } = useAsync(() => getExercises());
  const [group, setGroup] = useState<string | null>(null);
  const [q, setQ] = useState('');
  const [expandedId, setExpandedId] = useState<string | null>(null); // row showing its details
  const [extra, setExtra] = useState<Exercise[]>([]);
  const [creating, setCreating] = useState(false);
  const searchRef = useRef<HTMLInputElement>(null);

  const pool = useMemo(() => [...extra, ...(all ?? [])], [extra, all]);
  const list = useMemo(() => {
    const needle = q.trim().toLowerCase();
    return pool
      .filter((e) => (!group || e.muscleGroups.includes(group)) && (!needle || e.name.toLowerCase().includes(needle)))
      .slice(0, 60);
  }, [pool, group, q]);

  const canCreate = q.trim().length >= 2 && !pool.some((e) => e.name.toLowerCase() === q.trim().toLowerCase());

  async function createAndAdd() {
    setCreating(true);
    try {
      const created = await createCustomExercise({ name: q.trim(), muscleGroups: group ? [group] : [] });
      setExtra((prev) => [created, ...prev]);
      onAdd(created);
      setQ('');
    } catch {
      /* leave the field as-is so the coach can retry */
    } finally {
      setCreating(false);
    }
  }

  return (
    <Modal open onClose={onClose}>
      <div className="flex max-h-[80vh] w-full flex-col">
        <div className="flex items-center justify-between">
          <h2 className="font-display text-lg font-semibold text-ink">Add exercises</h2>
          <button type="button" aria-label="Close" onClick={onClose} className="text-muted hover:text-ink">
            <X className="size-5" aria-hidden />
          </button>
        </div>
        <p className="mt-1 text-sm text-muted">Search the library, or type an exercise that isn't there to add your own.</p>

        <div className="mt-3 flex flex-col gap-2">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-muted" aria-hidden />
            <input
              ref={searchRef}
              autoFocus
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search, or type your own exercise…"
              className="h-touch w-full rounded-control border border-line bg-elevated pl-9 pr-3 text-sm text-ink outline-none focus:border-ink"
            />
          </div>
          <div className="flex flex-wrap gap-1.5">
            <Chip label="All" active={!group} onClick={() => setGroup(null)} />
            {(filters?.muscleGroups ?? []).map((g) => (
              <Chip key={g} label={g} active={group === g} onClick={() => setGroup(g)} />
            ))}
          </div>
        </div>

        <div className="mt-3 min-h-0 flex-1 overflow-y-auto">
          {loading ? (
            <p className="py-8 text-center text-sm text-muted">Loading…</p>
          ) : list.length === 0 ? (
            <p className="py-8 text-center text-sm text-muted">No matches.</p>
          ) : (
            <ul className="flex flex-col divide-y divide-line">
              {list.map((e) => {
                const added = addedIds.has(e.id);
                const open = expandedId === e.id;
                const hasDetail = e.images.length > 0 || (e.instructions?.length ?? 0) > 0 || !!e.equipment;
                return (
                  <li key={e.id} className="py-2">
                    <div className="flex items-center gap-3">
                      <button
                        type="button"
                        onClick={() => hasDetail && setExpandedId(open ? null : e.id)}
                        className="size-16 shrink-0 overflow-hidden rounded-control bg-surface"
                        aria-label={hasDetail ? `Show ${e.name} details` : e.name}
                      >
                        {e.images[0] ? (
                          <img src={e.images[0]} alt="" loading="lazy" className="size-full object-cover" />
                        ) : (
                          <div className="flex size-full items-center justify-center text-muted">
                            <Dumbbell className="size-5" aria-hidden />
                          </div>
                        )}
                      </button>
                      <button
                        type="button"
                        onClick={() => hasDetail && setExpandedId(open ? null : e.id)}
                        className="min-w-0 flex-1 text-left"
                      >
                        <p className="truncate text-sm font-medium text-ink">{e.name}</p>
                        <div className="mt-0.5 flex flex-wrap items-center gap-1">
                          {e.muscleGroups.slice(0, 2).map((g) => (
                            <Badge key={g}>{g}</Badge>
                          ))}
                          {hasDetail && <span className="text-xs text-muted">{open ? 'Hide' : 'Details'}</span>}
                        </div>
                      </button>
                      <Button size="sm" variant={added ? 'ghost' : 'primary'} onClick={() => onAdd(e)} className={cn(added && 'text-ok-ink')}>
                        {added ? <Check className="size-4" aria-hidden /> : <Plus className="size-4" aria-hidden />}
                        {added ? 'Added' : 'Add'}
                      </Button>
                    </div>
                    {open && (
                      <div className="mt-2 rounded-control bg-surface p-3">
                        {e.images[0] && (
                          <img src={e.images[0]} alt={e.name} loading="lazy" className="mb-2 h-40 w-full rounded-control object-cover" />
                        )}
                        {e.equipment && <p className="text-xs text-muted">Equipment: <span className="text-ink">{e.equipment}</span></p>}
                        {(e.instructions?.length ?? 0) > 0 ? (
                          <ol className="mt-1.5 list-decimal space-y-1 pl-4 text-sm text-ink-soft">
                            {e.instructions.map((step, i) => (
                              <li key={i}>{step}</li>
                            ))}
                          </ol>
                        ) : (
                          <p className="mt-1 text-sm text-muted">No description for this exercise.</p>
                        )}
                      </div>
                    )}
                  </li>
                );
              })}
            </ul>
          )}
        </div>

        {canCreate ? (
          <div className="mt-2 rounded-control border border-dashed border-line-strong p-2.5">
            <p className="px-1 text-xs text-muted">
              “<span className="font-medium text-ink">{q.trim()}</span>” isn't in the library
            </p>
            <div className="mt-2 flex flex-col gap-1.5 sm:flex-row">
              <button
                type="button"
                onClick={() => {
                  onAddAdHoc(q.trim());
                  setQ('');
                }}
                className="flex-1 rounded-control bg-ink px-3 py-2 text-sm font-medium text-on-dark hover:bg-ink-soft"
              >
                Use just for this workout
              </button>
              <button
                type="button"
                onClick={createAndAdd}
                disabled={creating}
                className="flex-1 rounded-control border border-line bg-elevated px-3 py-2 text-sm font-medium text-ink hover:border-line-strong disabled:opacity-50"
              >
                {creating ? 'Adding…' : 'Add to library'}
              </button>
            </div>
          </div>
        ) : (
          // Always-visible nudge so coaches know they can add their own without guessing.
          <button
            type="button"
            onClick={() => searchRef.current?.focus()}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-control border border-dashed border-line-strong px-3 py-2.5 text-sm font-medium text-ink-soft hover:bg-surface hover:text-ink"
          >
            <Plus className="size-4" aria-hidden /> Can't find it? Type a name to add your own
          </button>
        )}

        <div className="mt-3 flex justify-end border-t border-line pt-3">
          <Button onClick={onClose}>Done</Button>
        </div>
      </div>
    </Modal>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={cn(
        'h-8 rounded-pill px-3 text-xs font-medium transition-colors',
        active ? 'bg-ink text-on-dark' : 'bg-surface text-ink-soft hover:bg-line',
      )}
    >
      {label}
    </button>
  );
}
